import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, CircleHelp, Download, RefreshCw, Search, SlidersHorizontal, X } from "lucide-react";
import type { Driver } from "driver.js";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import {
  EmptyState,
  InlineAlert,
  LoadingRows,
} from "@/components/admin/feedback";
import { PageFrame } from "@/components/admin/page-frame";
import { PageHeader } from "@/components/admin/page-header";
import { AuditChangeTable } from "@/components/admin/license-business-summary";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { adminApi, ApiError, type AuditFilters } from "@/lib/api";
import { useAuditQuery, useCurrentIdentityQuery } from "@/lib/queries";
import { useTimezone } from "@/lib/timezone";
import type { AuditEvent } from "@/lib/types";
import { cn, humanizeAction } from "@/lib/utils";
import { AUDIT_REVIEW_TOUR_ID, createAuditReviewTour } from "@/lib/workflow-tours";
import { restoreFocusAfterTour } from "@/lib/license-setup-tour";

type AuditDraft = {
  targetType: string;
  targetId: string;
  actorId: string;
  ip: string;
  action: string;
  outcome: string;
  correlationId: string;
  from: string;
  to: string;
};

const initialFilters: AuditDraft = {
  targetType: "",
  targetId: "",
  actorId: "",
  ip: "",
  action: "",
  outcome: "",
  correlationId: "",
  from: "",
  to: "",
};

// Suggestions improve discovery; the Combobox still accepts any exact API action.
const AUDIT_ACTION_SUGGESTIONS = [
  "activation.created",
  "activation.deactivated",
  "activation.refreshed",
  "admin.access_denied",
  "admin.account_created",
  "admin.account_role_changed",
  "admin.credentials_reset",
  "admin.login_failed",
  "admin.login_rate_limited",
  "admin.login_succeeded",
  "api_activity.exported",
  "api_activity.settings_updated",
  "audit.exported",
  "customer.created",
  "delivery_pack.exported",
  "license.created",
  "license.revoked",
  "license.updated",
  "product.created",
  "product.feature_catalog_updated",
  "product.status_updated",
  "product_key.batch_issued",
  "product_key.replaced",
  "product_key.revoked",
] as const;

const TARGET_TYPES = ["license", "activation", "customer", "product", "admin_account"] as const;

function toApiFilters(draft: AuditDraft): AuditFilters {
  return {
    target_type: draft.targetType,
    target_id: draft.targetId,
    actor_id: draft.actorId,
    ip: draft.ip,
    action: draft.action,
    outcome: (draft.outcome || undefined) as AuditFilters["outcome"],
    correlation_id: draft.correlationId,
    from: draft.from,
    to: draft.to,
  };
}

// Target pages that exist for an ID alone; other targets render as text.
function targetHref(event: AuditEvent) {
  if (event.target_type === "license") return `/admin/licenses/${encodeURIComponent(event.target_id)}`;
  if (event.target_type === "product") return `/admin/products/${encodeURIComponent(event.target_id)}/usage`;
  return undefined;
}

function shortId(value: string) {
  return value.length > 18 ? `${value.slice(0, 8)}…${value.slice(-6)}` : value;
}

export function AuditPage() {
  const { t } = useTranslation();
  const { timezoneAbbr, formatDateTime } = useTimezone();
  const [searchParams, setSearchParams] = useSearchParams();
  const identity = useCurrentIdentityQuery();
  const isSecurityAdmin = identity.data?.roles?.includes("Security Admin") ?? false;
  const tourRef = useRef<Driver | null>(null);
  const tourTriggerRef = useRef<HTMLButtonElement | null>(null);
  const [draft, setDraft] = useState<AuditDraft>(() => activityAuditFilters(searchParams) ?? initialFilters);
  const [submitted, setSubmitted] = useState<AuditDraft>(() => activityAuditFilters(searchParams) ?? initialFilters);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  const [invalidPivot, setInvalidPivot] = useState(() => activityAuditFilters(searchParams) === null);
  const query = useAuditQuery(toApiFilters(submitted), !invalidPivot);

  const events = useMemo(
    () => (invalidPivot ? [] : query.data?.pages.flatMap((page) => page.events) ?? []),
    [invalidPivot, query.data],
  );
  const filtersUnsubmitted = (Object.keys(initialFilters) as (keyof AuditDraft)[]).some(
    (key) => draft[key] !== submitted[key],
  );
  const actions = useMemo(
    () => [...new Set([...AUDIT_ACTION_SUGGESTIONS, ...events.map((event) => event.action)])].sort(),
    [events],
  );

  const clearTourRequest = useCallback(() => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete("tour");
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const startTour = useCallback(() => {
    const returnFocusTo = document.activeElement instanceof HTMLElement && document.activeElement !== document.body
      ? document.activeElement
      : tourTriggerRef.current;
    const activeTour = tourRef.current;
    tourRef.current = null;
    activeTour?.destroy();
    let tour: Driver;
    tour = createAuditReviewTour({ t, onDestroyed: () => {
      if (tourRef.current !== tour) return;
      tourRef.current = null;
      clearTourRequest();
      restoreFocusAfterTour(returnFocusTo);
    } });
    tourRef.current = tour;
    tour.drive();
  }, [clearTourRequest, t]);
  const tourRequested = searchParams.get("tour") === AUDIT_REVIEW_TOUR_ID;
  useEffect(() => {
    if (!tourRequested) return;
    const frame = window.requestAnimationFrame(startTour);
    return () => {
      window.cancelAnimationFrame(frame);
      tourRef.current?.destroy();
      tourRef.current = null;
    };
  }, [startTour, tourRequested]);

  function applyFilters(next: AuditDraft) {
    setInvalidPivot(false);
    setExpanded(null);
    setDraft(next);
    setSubmitted(next);
  }

  // The server applies the submitted filters, appends audit.exported, then
  // returns every matching event (up to its export limit) as CSV.
  async function exportEvents() {
    setExporting(true);
    setExportError("");
    try {
      const file = await adminApi.exportAuditEvents(toApiFilters(submitted));
      const url = URL.createObjectURL(file.bytes);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = file.filename;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setExportError(error instanceof ApiError && error.status === 422
        ? t("audit.exportTooLarge", "More than 10,000 events match. Narrow the filters and export again.")
        : t("audit.exportFailed", "Export failed. No file was created."));
    } finally {
      setExporting(false);
    }
  }

  const outcomeLabel = (outcome: string) => t(`audit.outcomes.${outcome}`, outcome);

  return (
    <PageFrame size="fluid">
      {invalidPivot && <InlineAlert title={t("audit.title")} message={t("audit.invalidActivityPivot")} />}
      <PageHeader
        title={t("audit.title", "Audit log")}
        description={t("audit.description", {
          tz: timezoneAbbr,
          defaultValue: `Filter evidence by target, actor, action, outcome, and ${timezoneAbbr} range. Export every matching event as CSV.`,
        })}
        actions={
          <>
            <Button
              data-tour-id="audit-export"
              variant="outline"
              onClick={() => void exportEvents()}
              disabled={events.length === 0 || exporting || invalidPivot}
            >
              <Download className="size-4" aria-hidden="true" />
              {exporting ? t("audit.exporting", "Exporting…") : t("common.exportCsv", "Export CSV")}
            </Button>
            <Button ref={tourTriggerRef} variant="ghost" onClick={startTour}>
              <CircleHelp className="size-4" aria-hidden="true" />
              {t("help.startGuidedTour", "Start guided tour")}
            </Button>
            <Button
              variant="outline"
              onClick={() => void query.refetch()}
              disabled={query.isFetching || invalidPivot}
            >
              <RefreshCw className="size-4" aria-hidden="true" />
              {query.isFetching ? t("common.refreshing", "Refreshing…") : t("common.refresh", "Refresh")}
            </Button>
          </>
        }
      />
      {exportError && <InlineAlert title={t("audit.exportErrorTitle", "Export not created")} message={exportError} />}

      <form
        className="flex shrink-0 flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          applyFilters(draft);
        }}
        role="search"
        aria-label={t("audit.filterAria", "Filter Audit events")}
      >
        <div data-tour-id="audit-target-actor" className="flex flex-wrap items-end gap-2">
          <Select
            className="w-44"
            aria-label={t("audit.targetType", "Target type")}
            value={draft.targetType}
            onChange={(event) => setDraft({ ...draft, targetType: event.target.value })}
          >
            <option value="">{t("audit.allTargetTypes", "All target types")}</option>
            {TARGET_TYPES.map((type) => (
              <option key={type} value={type}>{t(`audit.targetTypes.${type}`, type)}</option>
            ))}
          </Select>
          <label className="relative w-56">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted" aria-hidden="true" />
            <span className="sr-only">{t("audit.targetId", "Target ID")}</span>
            <Input
              className="pl-9"
              value={draft.targetId}
              onChange={(event) => setDraft({ ...draft, targetId: event.target.value })}
              placeholder={t("audit.targetIdPlaceholder", "target ID")}
            />
          </label>
          <Input
            className="w-40"
            value={draft.actorId}
            onChange={(event) => setDraft({ ...draft, actorId: event.target.value })}
            placeholder={t("audit.actorIdPlaceholder", "actor or username")}
            aria-label={t("audit.actorId", "Actor")}
          />
          {isSecurityAdmin && (
            <Input
              className="w-36"
              value={draft.ip}
              onChange={(event) => setDraft({ ...draft, ip: event.target.value })}
              placeholder={t("audit.ipPlaceholder", "IP address")}
              aria-label={t("audit.ip", "IP address")}
            />
          )}
        </div>
        <div data-tour-id="audit-action-time" className="flex flex-wrap items-end gap-2">
          <div className="w-56">
            <Combobox
              aria-label={t("audit.action", "Action")}
              value={draft.action}
              onValueChange={(action) => setDraft({ ...draft, action })}
              options={actions}
              placeholder={t("audit.actionPlaceholder", "All actions")}
            />
          </div>
          <Select
            className="w-36"
            aria-label={t("audit.outcome", "Outcome")}
            value={draft.outcome}
            onChange={(event) => setDraft({ ...draft, outcome: event.target.value })}
          >
            <option value="">{t("audit.allOutcomes", "Any outcome")}</option>
            {(["success", "denied", "failed"] as const).map((outcome) => (
              <option key={outcome} value={outcome}>{outcomeLabel(outcome)}</option>
            ))}
          </Select>
          <div className="w-48">
            <DateTimePicker
              id="audit-from"
              value={draft.from}
              onValueChange={(from) => setDraft({ ...draft, from })}
              aria-label={t("audit.startRangeAria", { tz: timezoneAbbr, defaultValue: `Start of audit range in ${timezoneAbbr}` })}
              placeholder={t("audit.from", { tz: timezoneAbbr, defaultValue: `From (${timezoneAbbr})` })}
            />
          </div>
          <div className="w-48">
            <DateTimePicker
              id="audit-to"
              value={draft.to}
              onValueChange={(to) => setDraft({ ...draft, to })}
              aria-label={t("audit.endRangeAria", { tz: timezoneAbbr, defaultValue: `End of audit range in ${timezoneAbbr}` })}
              placeholder={t("audit.to", { tz: timezoneAbbr, defaultValue: `To (${timezoneAbbr})` })}
            />
          </div>
        </div>
        <Button type="submit" variant="primary">
          <SlidersHorizontal className="size-4" aria-hidden="true" />
          {t("audit.filterEvents", "Filter events")}
        </Button>
        {draft.correlationId && (
          <span className="inline-flex h-9 items-center gap-2 text-xs text-muted">
            {t("audit.correlationFilter", "Correlation")}
            <span className="font-mono text-ink">{draft.correlationId}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t("audit.clearCorrelation", "Clear correlation filter")}
              onClick={() => setDraft({ ...draft, correlationId: "" })}
            >
              <X className="size-3.5" aria-hidden="true" />
            </Button>
          </span>
        )}
        {filtersUnsubmitted && (
          <p className="w-full text-xs text-warning" role="status">
            {t("audit.unsubmittedFilters", "Filters changed. Submit to update results.")}
          </p>
        )}
      </form>

      {query.isError ? (
        <InlineAlert
          title={t("audit.loadError", "Audit log could not load")}
          message={t("audit.loadErrorDescription", "Try again after checking the Admin API connection.")}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <section
          className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-line bg-panel"
          aria-labelledby="audit-events-heading"
        >
          <h2 id="audit-events-heading" className="sr-only">{t("audit.events", "Events")}</h2>
          {query.isPending && !invalidPivot ? (
            <div className="p-4"><LoadingRows /></div>
          ) : events.length === 0 ? (
            <EmptyState
              title={t("audit.noEvents", "No audit events found")}
              message={t("audit.noEventsDesc", "Adjust your filter criteria or clear the query to inspect recent privileged actions.")}
            />
          ) : (
            <div className="console-scrollbar min-h-0 flex-1 overflow-auto [scrollbar-gutter:stable]">
              <table className="w-full min-w-[1220px] table-fixed text-sm [overflow-wrap:anywhere] xl:min-w-[1360px]">
                <colgroup>
                  <col className="w-[60px] pointer-coarse:w-[68px]" />
                  <col className="w-[220px]" />
                  <col className="w-[100px]" />
                  <col className="w-[200px]" />
                  <col className="w-[28%]" />
                  <col className="w-[180px]" />
                  <col />
                  {/* Keep the detail span aligned while the IP cells are hidden. */}
                  <col className="w-0 xl:w-[140px]" />
                </colgroup>
                <thead className="sticky top-0 z-10 bg-panel text-[10px] uppercase tracking-[0.08em] text-muted">
                  <tr className="border-b border-line">
                    <th className="px-3 py-2.5"><span className="sr-only">{t("audit.details", "Details")}</span></th>
                    <th className="px-3 py-2.5 text-left font-normal">{t("audit.table.time", { tz: timezoneAbbr, defaultValue: `Time (${timezoneAbbr})` })}</th>
                    <th className="px-3 py-2.5 text-left font-normal">{t("audit.outcome", "Outcome")}</th>
                    <th className="px-3 py-2.5 text-left font-normal">{t("audit.table.action", "Action")}</th>
                    <th className="px-3 py-2.5 text-left font-normal">{t("audit.table.actorOnly", "Actor")}</th>
                    <th className="px-3 py-2.5 text-left font-normal">{t("audit.table.target", "Target")}</th>
                    <th className="px-3 py-2.5 text-left font-normal">{t("audit.table.reason", "Reason")}</th>
                    <th className="hidden px-3 py-2.5 text-left font-normal xl:table-cell">{t("audit.table.ipOnly", "IP")}</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => {
                    const open = expanded === event.id;
                    const href = targetHref(event);
                    const detailId = `audit-detail-${event.id}`;
                    return (
                      <Fragment key={event.id}>
                        <tr className={cn("border-b border-line align-top hover:bg-panel-raised/55", open && "bg-focus/10 hover:bg-focus/10")}>
                          <td className="px-3 py-2.5">
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-expanded={open}
                              aria-controls={detailId}
                              aria-label={t("audit.toggleDetails", "Show event details")}
                              onClick={() => setExpanded(open ? null : event.id)}
                            >
                              {open ? <ChevronDown className="size-4" aria-hidden="true" /> : <ChevronRight className="size-4" aria-hidden="true" />}
                            </Button>
                          </td>
                          <td className="whitespace-nowrap px-3 py-2.5 font-mono text-xs text-muted">
                            <time dateTime={event.created_at}>{formatDateTime(event.created_at)}</time>
                          </td>
                          <td className="whitespace-nowrap px-3 py-2.5">
                            <span className={cn("inline-flex items-center gap-1.5 text-xs", {
                              "text-success": event.outcome === "success",
                              "text-danger": event.outcome === "denied",
                              "text-warning": event.outcome === "failed",
                            })}>
                              <span className="size-1.5 rounded-full bg-current" aria-hidden="true" />
                              {outcomeLabel(event.outcome)}
                            </span>
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="font-medium text-ink">{humanizeAction(event.action)}</div>
                            <div className="font-mono text-xs text-muted">{event.action}</div>
                          </td>
                          <td className="px-3 py-2.5">
                            <div className={event.actor_name ? "text-ink" : "font-mono text-xs text-muted"}>{event.actor_name || event.actor_id}</div>
                            {event.actor_role && <div className="text-xs text-muted">{event.actor_role}</div>}
                          </td>
                          <td className="px-3 py-2.5">
                            {event.target_name ? (
                              href ? <Link className="text-focus hover:underline" to={href}>{event.target_name}</Link> : <span className="text-ink">{event.target_name}</span>
                            ) : href ? (
                              <Link className="text-focus hover:underline" to={href}>{t(`audit.targetTypes.${event.target_type}`, event.target_type)}</Link>
                            ) : (
                              <span className="text-muted">{t(`audit.targetTypes.${event.target_type}`, event.target_type)}</span>
                            )}
                            <div className="font-mono text-xs text-muted" title={event.target_id}>{shortId(event.target_id)}</div>
                          </td>
                          <td className="max-w-64 px-3 py-2.5 text-xs text-muted">
                            <span className="line-clamp-2 break-words">{event.reason || t("audit.table.reasonNotRecorded", "No reason recorded")}</span>
                          </td>
                          <td className="hidden px-3 py-2.5 font-mono text-xs text-muted xl:table-cell">
                            {event.ip || t("audit.table.notRecorded", "not recorded")}
                          </td>
                        </tr>
                        {open && (
                          <tr id={detailId} className="border-b border-line bg-canvas/40">
                            <td colSpan={8} className="px-4 py-4 sm:pl-12">
                              <div className="grid gap-6 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
                                <div className="min-w-0">
                                  <h3 className="mb-1.5 text-[10px] uppercase tracking-[0.08em] text-muted">{t("audit.changes", "Changes")}</h3>
                                  <AuditChangeTable before={event.before} after={event.after} />
                                </div>
                                <div className="min-w-0">
                                  <h3 className="mb-1.5 text-[10px] uppercase tracking-[0.08em] text-muted">{t("audit.context", "Context")}</h3>
                                  <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-1 text-xs">
                                    <dt className="text-muted">{t("audit.eventId", "Event ID")}</dt><dd className="break-all font-mono">{event.id}</dd>
                                    <dt className="text-muted">{t("audit.correlation", "Correlation")}</dt><dd className="break-all font-mono">{event.correlation_id}</dd>
                                    <dt className="text-muted">{t("audit.actorIdLabel", "Actor ID")}</dt><dd className="break-all font-mono">{event.actor_id}</dd>
                                    <dt className="text-muted">{t("audit.recordedUtc", "Recorded (UTC)")}</dt><dd className="break-all font-mono">{event.created_at}</dd>
                                    <dt className="text-muted xl:hidden">{t("audit.table.ipOnly", "IP")}</dt><dd className="break-all font-mono xl:hidden">{event.ip || t("audit.table.notRecorded", "not recorded")}</dd>
                                    <dt className="text-muted">{t("audit.table.reason", "Reason")}</dt><dd className="break-words">{event.reason || t("audit.table.reasonNotRecorded", "No reason recorded")}</dd>
                                  </dl>
                                  <div className="mt-3 flex flex-wrap gap-2">
                                    {event.correlation_id && (
                                      <Button size="sm" variant="outline" onClick={() => applyFilters({ ...initialFilters, correlationId: event.correlation_id })}>
                                        {t("audit.sameCorrelation", "Same correlation")}
                                      </Button>
                                    )}
                                    <Button size="sm" variant="outline" className="h-auto min-h-9 max-w-full whitespace-normal" onClick={() => applyFilters({ ...initialFilters, actorId: event.actor_id })}>
                                      {t("audit.moreByActor", { actor: event.actor_name || event.actor_id, defaultValue: `More by ${event.actor_name || event.actor_id}` })}
                                    </Button>
                                    {isSecurityAdmin && event.correlation_id && (
                                      <Link className={buttonVariants({ size: "sm", variant: "outline" })} to={`/admin/api-activity?correlation_id=${encodeURIComponent(event.correlation_id)}`}>
                                        {t("audit.openApiActivity", "Open in API Activity")}
                                      </Link>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {events.length > 0 && (
            <div className="flex shrink-0 items-center justify-between gap-3 border-t border-line px-4 py-2.5 text-xs text-muted">
              <Badge variant="neutral">
                {t("audit.eventsReturned", { count: events.length, defaultValue: `${events.length} loaded` })}
              </Badge>
              {query.hasNextPage ? (
                <Button size="sm" variant="outline" onClick={() => void query.fetchNextPage()} disabled={query.isFetchingNextPage}>
                  {query.isFetchingNextPage ? t("audit.loadingMore", "Loading…") : t("audit.loadMore", "Load more")}
                </Button>
              ) : (
                <span>{t("audit.allLoaded", "All matching events loaded")}</span>
              )}
            </div>
          )}
        </section>
      )}
    </PageFrame>
  );
}

// Activity pivots carry only canonical entity IDs and an event-time range.
function activityAuditFilters(params: URLSearchParams): AuditDraft | null {
  if (params.get("source") !== "api_activity") return initialFilters;
  const targetId = params.get("target_id") ?? "";
  const targetType = params.get("target_type") ?? "";
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";
  if ((!targetId || new TextEncoder().encode(targetId).length > 256) || !["activation", "license", "product"].includes(targetType) ||
      !Number.isFinite(Date.parse(from)) || !Number.isFinite(Date.parse(to)) || Date.parse(from) >= Date.parse(to)) return null;
  return { ...initialFilters, targetId, targetType, from, to };
}
